/**
 * Core types for the auth state machine.
 *
 * The state machine is intentionally minimal — six states, twelve events —
 * so every transition can be enumerated and unit-tested. Side effects
 * (adapter calls, persistence, refresh scheduling) live in the runtime,
 * not here.
 */

/** Normalised user identity carried inside an authenticated session. */
export interface AuthUser {
    /** Stable internal id (AOS aosId claim). */
    id: number
    /** Display name from the IdP. */
    name: string
    /** Optional human-friendly display name (falls back to `name`). */
    displayName?: string
    /** Firebase UID for chat / push features. */
    firebaseId?: string
    /** Email address (if released by the IdP). */
    email?: string
}

/** Claims surfaced by an IdP adapter after a successful token exchange. */
export interface IdPClaims {
    user: AuthUser
    roles: readonly string[]
    /**
     * IATA codes of the airports the user belongs to, from the `groups`
     * claim (`/airports/{IATA}` entries). Empty for adapters/tenants that
     * don't emit airport groups.
     */
    airports?: readonly string[]
    /** Absolute expiry time, milliseconds since epoch. */
    expiresAt: number
    /** Free-form bag for adapter-specific data (tenant, scope, …). */
    extra?: Readonly<Record<string, unknown>>
}

/** A live session — the token bundle plus the claims derived from it. */
export interface Session {
    accessToken: string
    refreshToken?: string
    idToken?: string
    claims: IdPClaims
}

/** Why we landed in `unauthenticated`. Drives logging + UX copy only. */
export type UnauthenticatedReason = 'init' | 'logout' | 'session-lost'

/**
 * State machine states.
 *
 * Transitions:
 *
 *     idle
 *       └─ INIT_STARTED         → initializing
 *     initializing
 *       ├─ INIT_RESOLVED(s)     → authenticated(s)   when s
 *       └─ INIT_RESOLVED(null)  → unauthenticated('init')
 *     unauthenticated
 *       └─ LOGIN_STARTED        → authenticating
 *     authenticating
 *       ├─ LOGIN_SUCCEEDED(s)   → authenticated(s)
 *       └─ LOGIN_FAILED(e)      → unauthenticated('init')
 *     authenticated
 *       ├─ REFRESH_STARTED(p)   → refreshing(s, p)
 *       ├─ LOGOUT_STARTED       → unauthenticated('logout')  (via LOGOUT_COMPLETED)
 *       └─ SESSION_INVALIDATED  → reauthRequired
 *     refreshing
 *       ├─ REFRESH_SUCCEEDED(s) → authenticated(s)
 *       ├─ REFRESH_FAILED_TRANSIENT(e) → authenticated(prev) (hold session, retry later)
 *       └─ REFRESH_FAILED_TERMINAL(e)  → reauthRequired
 *     reauthRequired
 *       └─ LOGIN_STARTED        → authenticating
 */
export type AuthState =
    | { tag: 'idle' }
    | { tag: 'initializing' }
    | { tag: 'unauthenticated'; reason: UnauthenticatedReason }
    | { tag: 'authenticating' }
    | { tag: 'authenticated'; session: Session }
    | { tag: 'refreshing'; session: Session; refreshPromise: Promise<Session> }
    | { tag: 'reauthRequired'; lastError: Error }

export type AuthEvent =
    | { type: 'INIT_STARTED' }
    | { type: 'INIT_RESOLVED'; session: Session | null }
    | { type: 'LOGIN_STARTED' }
    | { type: 'LOGIN_SUCCEEDED'; session: Session }
    | { type: 'LOGIN_FAILED'; error: Error }
    | { type: 'REFRESH_STARTED'; promise: Promise<Session> }
    | { type: 'REFRESH_SUCCEEDED'; session: Session }
    | { type: 'REFRESH_FAILED_TRANSIENT'; error: Error }
    | { type: 'REFRESH_FAILED_TERMINAL'; error: Error }
    | { type: 'LOGOUT_STARTED' }
    | { type: 'LOGOUT_COMPLETED' }
    | { type: 'SESSION_INVALIDATED'; error: Error }

/** Public, ergonomic view of the state — what `useAuth().status` returns. */
export type AuthStatus = AuthState['tag']

export const initialAuthState: AuthState = { tag: 'idle' }
